// IconButton. Square button with icon only. Used in view headers
// (SaleView, etc.) where today 40x40 buttons are repeated inline.
// See DESIGN.md §9.15 (header actions) and §11.2 (focus).

import type { CSSProperties, ReactNode } from "react";
import { Link } from "react-router-dom";

export type IconButtonVariant = "ghost" | "outline" | "solid" | "soft" | "danger";
export type IconButtonSize = "sm" | "md" | "lg";

type Props = {
  icon: ReactNode;
  label: string;
  text?: ReactNode;
  variant?: IconButtonVariant;
  size?: IconButtonSize;
  onClick?: () => void;
  to?: string;
  active?: boolean;
  badge?: ReactNode;
  disabled?: boolean;
  style?: CSSProperties;
  className?: string;
};

const sizeConfig: Record<
  IconButtonSize,
  { box: number; icon: number; radius: string }
> = {
  sm: { box: 32, icon: 16, radius: "var(--radius-md)" },
  md: { box: 40, icon: 20, radius: "var(--radius-md)" },
  lg: { box: 48, icon: 24, radius: "var(--radius-md)" },
};

function variantStyle(
  variant: IconButtonVariant,
  active: boolean
): CSSProperties {
  if (variant === "solid") {
    return {
      background: "var(--surface-inverse)",
      color: "var(--text-inverse)",
      border: "1px solid var(--surface-inverse)",
    };
  }
  if (variant === "outline") {
    return {
      background: "transparent",
      color: "var(--text-primary)",
      border: "1px solid var(--border-default)",
    };
  }
  if (variant === "soft") {
    return {
      background: "var(--surface-sunken)",
      color: "var(--text-brand)",
      border: "1px solid transparent",
    };
  }
  if (variant === "danger") {
    return {
      background: "var(--color-danger-soft)",
      color: "var(--color-danger)",
      border: "1px solid transparent",
    };
  }
  // ghost (default)
  return {
    background: active ? "var(--surface-sunken)" : "transparent",
    color: "var(--text-primary)",
    border: "1px solid transparent",
  };
}

export function IconButton({
  icon,
  label,
  text,
  variant = "ghost",
  size = "md",
  onClick,
  to,
  active = false,
  badge,
  disabled,
  style,
  className,
}: Props) {
  const cfg = sizeConfig[size];
  const boxStyle: CSSProperties = {
    position: "relative",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: cfg.box,
    height: cfg.box,
    borderRadius: cfg.radius,
    cursor: disabled ? "not-allowed" : "pointer",
    opacity: disabled ? 0.4 : 1,
    transition: "background 120ms, color 120ms, border-color 120ms",
    textDecoration: "none",
    ...variantStyle(variant, active),
    ...style,
  };

  const wrappedIcon = (
    <span
      aria-hidden="true"
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: cfg.icon,
        height: cfg.icon,
      }}
    >
      {icon}
    </span>
  );

  const badgeNode = badge ? (
    <span
      style={{
        position: "absolute",
        top: -2,
        right: -2,
        pointerEvents: "none",
      }}
    >
      {badge}
    </span>
  ) : null;
  const textNode = text ? <span className="icon-button__text">{text}</span> : null;

  if (to != null) {
    return (
      <Link to={to} aria-label={label} title={label} style={boxStyle} className={className}>
        {wrappedIcon}
        {textNode}
        {badgeNode}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      disabled={disabled}
      style={boxStyle}
      className={className}
    >
      {wrappedIcon}
      {textNode}
      {badgeNode}
    </button>
  );
}
